import type { SourceIdentity } from '../lib/flow-validator-api';

/**
 * SourceChip (ADR-095) — the acquisition-source identity that replaces the old
 * Referrer column in the Flow Validator: a brand glyph + platform name, with a
 * "· in-app (inferred)" sub-label when the identity was recovered from an in-app
 * browser user-agent. Look copied from mockup-flow-validator-sessions-v4.
 *
 * Glyphs are a curated Simple Icons subset, vendored inline (no external fetch —
 * the portal CSP forbids it). LinkedIn / Instagram / Direct / Embed-probe reuse
 * the mock's CSS-drawn glyphs; the remaining brands render an inline Simple Icons
 * path. An unknown referrer host falls back to a generic globe.
 */

// Vendored Simple Icons (24×24) single-path marks — retrieved from simple-icons,
// not hand-authored (failure mode #12).
const ICON_PATHS: Record<string, string> = {
  facebook:
    'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
  x:
    'M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z',
  youtube:
    'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
  tiktok:
    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  google:
    'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z',
  reddit:
    'M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z',
};

// Brand fill; black-brand marks (x, tiktok) render light so they show on the
// dark chip.
const ICON_FILL: Record<string, string> = {
  facebook: '#1877F2', youtube: '#FF0000', google: '#4285F4', reddit: '#FF4500',
  x: '#e7e9ea', tiktok: '#e7e9ea',
};

function BrandGlyph({ platform, kind }: { platform: string; kind: SourceIdentity['kind'] }) {
  if (kind === 'synthetic' || platform === 'probe') {
    return <span className="glyph glyph-probe" aria-hidden="true">⚙</span>;
  }
  if (kind === 'direct') {
    return <span className="glyph glyph-direct" aria-hidden="true">—</span>;
  }
  // ADR-103 D1 — MACHINE identities (ClaudeBot, Googlebot, …). Where a vendor
  // has a mark we already vendor (Google), reuse it; otherwise a neutral glyph
  // distinguishes an LLM read from a crawler at a glance.
  //
  // NOTE: the mock draws these with google.com/s2/favicons. That is a remote
  // fetch and the portal CSP forbids it, so the appearance is honoured with
  // local marks rather than by loosening the CSP for decoration.
  if (kind === 'llm' || kind === 'crawler') {
    const d = ICON_PATHS[platform];
    if (d) {
      return (
        <svg viewBox="0 0 24 24" fill={ICON_FILL[platform] ?? 'currentColor'} aria-hidden="true">
          <path d={d} />
        </svg>
      );
    }
    return (
      <span className={`glyph glyph-machine glyph-${kind}`} aria-hidden="true">
        {kind === 'llm' ? '✦' : '⌗'}
      </span>
    );
  }
  if (platform === 'linkedin') {
    return <span className="glyph glyph-li" aria-hidden="true">in</span>;
  }
  if (platform === 'instagram') {
    return <span className="glyph glyph-ig" aria-hidden="true" />;
  }
  const d = ICON_PATHS[platform];
  if (d) {
    return (
      <svg viewBox="0 0 24 24" fill={ICON_FILL[platform] ?? 'currentColor'} aria-hidden="true">
        <path d={d} />
      </svg>
    );
  }
  // Unknown referrer host (or a referral code) → generic globe.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );
}

/** A short human explanation of how the identity was resolved (chip title). */
function chipTitle(id: SourceIdentity): string {
  switch (id.kind) {
    case 'synthetic': return `Platform-generated probe traffic — ${id.label} (source: synthetic)`;
    case 'llm': return `${id.label} — an AI assistant or model fetching the page. Identified at the edge from the User-Agent cross-checked against the network operator.`;
    case 'crawler': return `${id.label} — a search or SEO crawler. Identified at the edge from the User-Agent cross-checked against the network operator.`;
    case 'referrer': return `Resolved from the referrer domain → ${id.label}`;
    case 'utm': return `Resolved from utm_source → ${id.label}`;
    case 'inference': return `No referrer, no UTM — ${id.label} in-app browser detected from the user-agent (inferred)`;
    case 'referral': return `Referral: ${id.label}`;
    default: return 'No referrer, no UTM, standard browser — genuinely direct';
  }
}

export function SourceChip({ identity }: { identity: SourceIdentity }) {
  return (
    <span className="src-chip" title={chipTitle(identity)}>
      <BrandGlyph platform={identity.platform} kind={identity.kind} />
      {identity.label}
      {identity.inferred && <span className="sub">· in-app (inferred)</span>}
    </span>
  );
}
